import { DEFAULT_REVIEW_SORT } from '@/store/consts';
import { capitalize } from "@/utils/string-utils";

export default {
  namespaced: true,

  state: () => ({
    item: {},
    colors: [],
    activeColor: '',
    loading: false,
    availability: [],
    // TODO: вынести в отдельный модуль отзывы
    // https://jira.app.local/browse/ESMFUN-673
    reviews: [],
    reviewProductSummary: {},
    reviewsMeta: {},
    isReviewsAvailable: false,
  }),

  getters: {
    item: state => state.item,
    colors: state => state.colors,
    activeColor: state => state.activeColor,
    sizes: state => state.item?.skus || [],
    activeSku: (state, getters, rootState) => {
      return getters.sizes.find(sku => sku.available && sku.id === rootState?.route?.query['sku-id']) || {};
    },
    loading: state => state.loading,
    availability: state => state.availability || [],
    reviewProductSummary: state => state.reviewProductSummary || {},
    productReviews: state => state.reviews,
    reviewsMeta: state => state.reviewsMeta,
    hasMoreReviewsPages: (state, getters ) => {
      const { total_pages, current_page } = getters.reviewsMeta;

      return current_page < total_pages;
    },
    currentReviewsPage: (state, getters ) => getters.reviewsMeta?.current_page || 0,
    reviewsСount: (state, getters) => getters.reviewProductSummary?.reviews_count || 0,
    reviewRating: (state, getters) => getters.reviewProductSummary?.rating || 0,
  },

  actions: {
    async loadProduct({ rootState, state, getters, commit }, {
      productId = rootState?.route?.params?.id,
      size = getters?.activeSku?.size,
      routePush = true } = {}) {
      commit('loading');
      const product = await this.$api.loadProduct({ productId });
      commit('setProduct', product);

      // выставить размер от предыдущего товара
      if (size && routePush) {
        const sku = product.skus.find(sku => sku.available && sku.size === size);

        if (sku) this.$router.push({ query: { 'sku-id': sku.id }});
      }

      if (!state.colors.length) {
        // первая загрузка карточки товара на ssr
        // сетим их отдельно, чтобы не прыгали при изменении
        commit('setColors', product.models?.map(m => ({
          ...m.color,
          modelId: m.id,
          name: capitalize(m.color?.name)
        })) || []);
        commit('setActiveColor', product.id);
      }

      commit('loading', false);
    },

    chooseColor({ commit, dispatch }, productId) {
      commit('setActiveColor', productId);

      return Promise.allSettled([
        dispatch('loadProduct', { productId }),
        dispatch('loadAvailabilityList', { productId }),
      ]);
    },

    loadAvailabilityList({ rootState, commit }, { productId = rootState?.route?.params?.id } = {}) {
      return this.$api.loadAvailabilityList({ productId })
        .then(data => {
          commit('setAvailableList', data);
        });
    },

    async getCountReviews({ commit, rootState }, { productId = rootState.route.params.id } = {}) {
      if (!productId) return;

      try {
        const { count } = await this.$api.getCountReviews({ productId });

        commit('setReviewProductSummary', { reviews_count: count });
      } catch (error) {
        console.error(error);
      }
    },

    async loadProductReviews({ commit }, { productId, sort = DEFAULT_REVIEW_SORT } = {}) {
      if (!productId) {
        throw new Error('не пепредан productId');
      }

      try {
        const { meta, summary, reviews } = await this.$api.getReviews({ productId, page: 1, sort });

        commit('setReviews', reviews);
        commit('setReviewProductSummary', summary);
        commit('setReviewsMeta', meta);
      } catch (error) {
        console.error(error);
      }
    },

    async loadMoreReviews({ commit, getters }, { productId,  sort } = {}) {
      if (!productId) return;

      try {
        const page = getters.currentReviewsPage + 1;
        const { reviews, meta } = await this.$api.getReviews({ productId, page, sort });

        commit('addReviews', reviews);
        commit('setReviewsMeta', meta);
      } catch (error) {
        console.error(error);
      }
    },

    async voteForReview({ rootGetters }, { reviewId, isLike = true } = {}) {
      const voterId = rootGetters['user/account']?.id;

      if (!voterId || !reviewId) {
        throw new Error('Не переданы reviewId и voterId')
      }

      try {
        const result = await this.$api.voteForReview({ reviewId, isLike, voterId });

        if (!result.success && result.errors) {
          const error = Object.entries(result.errors)[0][1];

          return Promise.reject(error);
        } else if (!result.success && !result.errors){
          return Promise.reject();
        }
      } catch (error) {
        console.error(error);
        return Promise.reject();
      }
    },

    async addReview({ dispatch, rootGetters, getters }, {
      rating,
      fitRating,
      qualityRating,
      photoAccuracyRating,
      body,
      pros,
      cons,
      recommended,
      authorName,
      locationName,
      dimensions,
      timeUsage,
    } = {}) {
      const showError = () => dispatch('notifications/create',{
        title: 'Произошла ошибка',
        message: 'Попробуйте позже',
      },{ root: true});

      try {
        const result = await this.$api.addReview({
          rating,
          fitRating,
          qualityRating,
          photoAccuracyRating,
          body,
          pros,
          cons,
          recommended,
          authorId: rootGetters['user/account']?.id,
          authorName,
          authorEmail: rootGetters['user/account']?.email,
          authorPhone: rootGetters['user/account']?.phone,
          locationName,
          productId: getters.item?.id,
          dimensions,
          timeUsage,
        });

        if (result.success) {
          dispatch('notifications/create',{
            title: 'Спасибо за ваш отзыв',
            message: 'Он будет отправлен на модерацию',
            type: 'success',
          }, { root: true})
        } else {
          if (result?.errors?.content_hash?.[0]?.error === 'taken') {
            dispatch('notifications/create',{
              title: 'Произошла ошибка',
              message: 'Вы уже отправляли такой отзыв',
            }, {root: true});

          } else {
            showError();
          }
        }

      } catch (error) {
        showError();
      }
    },

    async getReviewsServiceState({ commit }) {
      try {
        const { data } = await this.$api.checkReviewsAvailable();
        commit('setIsReviewsAvailable', data?.isAvailable);
      } catch(e) {
        console.error(e);
      }
    }
  },

  mutations: {
    setProduct: (state, product = {}) => {
      state.item = product;
    },

    setColors: (state, colors = []) => {
      state.colors = colors;
    },

    setActiveColor: (state, color = {}) => {
      state.activeColor = color;
    },

    loading: (state, loading = true) => {
      state.loading = loading;
    },

    setAvailableList: (state, payload = {}) => {
      state.availability = payload;
    },

    setReviews(state, reviews) {
      state.reviews = reviews;
    },

    addReviews(state, reviews) {
      state.reviews.push(...reviews);
    },

    setReviewProductSummary(state, reviewProductSummary) {
      state.reviewProductSummary = reviewProductSummary;
    },

    setReviewsMeta(state, reviewsMeta) {
      state.reviewsMeta = reviewsMeta;
    },

    setIsReviewsAvailable(state, isAvailable) {
      state.isReviewsAvailable = isAvailable;
    },
  },
};
